import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import AntButton from 'antd/es/button';
import { cn } from 'app/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'link';
export type ButtonSize = 'md' | 'sm' | 'lg';

export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'color'
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  /** When true, square padding for icon-only controls */
  iconOnly?: boolean;
  /** Native button type (antd: `htmlType`) */
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  icon?: ReactNode;
}

/**
 * App-level button façade over antd; keeps verify-document-v2 prop names stable.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      loading = false,
      iconOnly = false,
      block = false,
      disabled,
      className,
      children,
      type: htmlType = 'button',
      ...rest
    },
    ref
  ) => {
    const antType =
      variant === 'primary' || variant === 'destructive'
        ? 'primary'
        : variant === 'ghost'
          ? 'text'
          : variant === 'link'
            ? 'link'
            : 'default';
    const antSize =
      size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'middle';

    return (
      <AntButton
        ref={ref}
        type={antType}
        danger={variant === 'destructive'}
        htmlType={htmlType}
        loading={loading}
        disabled={disabled}
        size={antSize}
        block={block}
        shape={iconOnly ? 'circle' : 'default'}
        className={cn(className)}
        {...rest}
      >
        {children}
      </AntButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
